import { createReducer, on } from '@ngrx/store';
import * as CourseCommentsActions from './course-comments.actions';

export interface CourseCommentsState {
  comments: any[];
  isLoading: boolean;
  isAddingComment: boolean;
  error: any;
}

export const initialState: CourseCommentsState = {
  comments: [],
  isLoading: false,
  isAddingComment: false,
  error: null,
};

export const courseCommentsReducer = createReducer(
  initialState,
  on(CourseCommentsActions.loadComments, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(CourseCommentsActions.loadCommentsSuccess, (state, { comments }) => ({
    ...state,
    isLoading: false,
    comments,
  })),
  on(CourseCommentsActions.loadCommentsFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),
  on(CourseCommentsActions.addComment, (state) => ({
    ...state,
    isAddingComment: true,
    error: null,
  })),
  on(CourseCommentsActions.addCommentSuccess, (state, { comment }) => ({
    ...state,
    isAddingComment: false,
    comments: addCommentToTree(state.comments, comment),
  })),
  on(CourseCommentsActions.addCommentFailure, (state, { error }) => ({
    ...state,
    isAddingComment: false,
    error,
  })),
  // Update
  on(CourseCommentsActions.updateCommentSuccess, (state, { comment }) => ({
    ...state,
    comments: updateCommentInTree(state.comments, comment),
  })),
  // Delete
  on(CourseCommentsActions.deleteCommentSuccess, (state, { commentId }) => ({
    ...state,
    comments: deleteCommentFromTree(state.comments, commentId),
  })),
  // Local Update
  on(
    CourseCommentsActions.updateCommentLocal,
    (state, { commentId, changes }) => ({
      ...state,
      comments: updateCommentInTree(state.comments, {
        id: commentId,
        ...changes,
      }),
    }),
  ),
);

// Helper Functions
function addCommentToTree(nodes: any[], newComment: any): any[] {
  const parentId =
    newComment.comment_parent || newComment.parent || newComment.parentId;

  // If it's a root comment (no parent), prepend to the list
  if (!parentId || parentId == '0' || parentId == 0) {
    return [newComment, ...nodes];
  }

  // Recursive search to find the parent
  return nodes.map((node) => {
    if (node.id == parentId || node.id == parentId?.id) {
      // Found parent, append to its replies
      return {
        ...node,
        replies: node.replies ? [...node.replies, newComment] : [newComment],
      };
    } else if (node.replies) {
      // Check children
      return {
        ...node,
        replies: addCommentToTree(node.replies, newComment),
      };
    }
    return node;
  });
}

function updateCommentInTree(nodes: any[], updatedComment: any): any[] {
  return nodes.map((node) => {
    if (node.id == updatedComment.id) {
      // Found the comment, merge only the changed fields
      // Only update fields that are explicitly provided (not undefined)
      const updates: any = {};
      if (updatedComment.content !== undefined)
        updates.content = updatedComment.content;
      if (updatedComment.status !== undefined)
        updates.status = updatedComment.status;
      if (updatedComment.approved !== undefined)
        updates.approved = updatedComment.approved;
      if (updatedComment.read_status !== undefined)
        updates.read_status = updatedComment.read_status;
      if (updatedComment.updated_at !== undefined)
        updates.updated_at = updatedComment.updated_at;

      return {
        ...node,
        ...updates,
        // Ensure replies are preserved from the current node state
        replies: node.replies,
      };
    } else if (node.replies) {
      // Check children
      return {
        ...node,
        replies: updateCommentInTree(node.replies, updatedComment),
      };
    }
    return node;
  });
}

function deleteCommentFromTree(nodes: any[], commentId: string): any[] {
  return nodes
    .filter((node) => node.id !== commentId) // Remove from current level
    .map((node) => {
      if (node.replies) {
        // Recursively remove from children
        return {
          ...node,
          replies: deleteCommentFromTree(node.replies, commentId),
        };
      }
      return node;
    });
}
